import { Modal, message } from 'antd';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Course from '../../course/types/Course';
import OpenClassAPI from '../OpenClassAPI';
import {
  OpenClass,
  OpenClassSession,
  OpenClassStatus,
} from '../types/OpenClass';
import SessionFields from './SessionFields';
import {
  SessionDraft,
  draftFromSession,
  emptySessionDraft,
  isSessionDraftValid,
  sessionDraftToCreateParams,
  sessionDraftToUpdateParams,
} from './sessionDraft';
import {
  findSessionOverlapPairs,
  formatSessionDateTimeRange,
} from './sessionTiming';

type OpenClassSessionFormModalProps = {
  open: boolean;
  openClassId: OpenClass['id'];
  courseId: Course['id'];
  existingSessions: OpenClassSession[];
  session: OpenClassSession | null;
  onClose: () => void;
  onSaved: () => void;
};

const OpenClassSessionFormModal: React.FC<OpenClassSessionFormModalProps> = ({
  open,
  openClassId,
  courseId,
  existingSessions,
  session,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [modal, modalContextHolder] = Modal.useModal();
  const [draft, setDraft] = useState<SessionDraft>(emptySessionDraft());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(session ? draftFromSession(session) : emptySessionDraft());
    }
  }, [open, session]);

  const saveSession = useCallback(
    async (draftToSave: SessionDraft) => {
      setSubmitting(true);
      try {
        if (session) {
          await OpenClassAPI.updateSession(
            openClassId,
            session.id,
            sessionDraftToUpdateParams(draftToSave, draftFromSession(session)),
          );
        } else {
          await OpenClassAPI.createSession(
            openClassId,
            sessionDraftToCreateParams(draftToSave),
          );
        }
        onSaved();
        onClose();
      } catch (error) {
        if (isAxiosError(error) && !error.response?.data.message) {
          message.error(t('views.OpenClasses.saveFailed'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onClose, onSaved, openClassId, session, t],
  );

  const submit = useCallback(() => {
    if (!isSessionDraftValid(draft)) {
      message.error(t('views.OpenClasses.invalidSession'));
      return;
    }

    const sessions = existingSessions.map((existingSession) =>
      existingSession.id === session?.id
        ? draft
        : draftFromSession(existingSession),
    );
    if (!session) {
      sessions.push(draft);
    }
    const overlapPairs = findSessionOverlapPairs(sessions);

    const saveWithOverlapCheck = () => {
      if (overlapPairs.length === 0) {
        void saveSession(draft);
        return;
      }

      modal.confirm({
        cancelText: t('common.cancel'),
        content: (
          <div>
            <div className="space-y-2">
              {overlapPairs.map(([firstIndex, secondIndex]) => (
                <div key={`${firstIndex}-${secondIndex}`}>
                  {t('views.OpenClasses.overlapPair', {
                    firstNumber: firstIndex + 1,
                    firstRange: formatSessionDateTimeRange(
                      sessions[firstIndex],
                    ),
                    secondNumber: secondIndex + 1,
                    secondRange: formatSessionDateTimeRange(
                      sessions[secondIndex],
                    ),
                  })}
                </div>
              ))}
            </div>
            <p className="mb-0 mt-4">
              {t('views.OpenClasses.overlapConfirmation')}
            </p>
          </div>
        ),
        okText: session ? t('common.save') : t('common.create'),
        okType: 'primary',
        title: t('views.OpenClasses.overlapTitle'),
        onOk: () => saveSession(draft),
      });
    };

    const newlyCanceled =
      draft.canceled && session?.status !== OpenClassStatus.Canceled;
    if (newlyCanceled) {
      modal.confirm({
        cancelText: t('common.cancel'),
        content: t('views.OpenClasses.cancelSessionConfirm'),
        okButtonProps: { danger: true },
        okText: t('common.confirm'),
        title: t('views.OpenClasses.cancelSessionTitle'),
        onOk: saveWithOverlapCheck,
      });
      return;
    }

    saveWithOverlapCheck();
  }, [draft, existingSessions, modal, saveSession, session, t]);

  return (
    <>
      <Modal
        destroyOnClose
        confirmLoading={submitting}
        open={open}
        title={
          session
            ? t('views.OpenClasses.editSession')
            : t('views.OpenClasses.addSession')
        }
        width={1200}
        onCancel={onClose}
        onOk={submit}>
        <SessionFields
          courseId={courseId}
          showInstructorSearch
          value={draft}
          onChange={setDraft}
        />
      </Modal>
      {modalContextHolder}
    </>
  );
};

export default OpenClassSessionFormModal;
